import React, { useState } from 'react';

interface FAQItem {
  q: string;
  a: string;
}

interface PageFAQProps {
  title?: string;
  items: FAQItem[];
  accentColor?: string;
  hidden?: boolean;
}

export const PageFAQ: React.FC<PageFAQProps> = ({
  title = 'Preguntas frecuentes',
  items,
  accentColor = '#34D399',
  hidden = false,
}) => {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section
      style={{
        background: 'var(--faq-bg, #0f172a)',
        borderTop: '1px solid var(--faq-border, #1e293b)',
        padding: '48px 24px 64px',
        marginTop: '0',
        display: hidden ? 'none' : undefined,
      }}
      aria-label="Preguntas frecuentes"
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <div style={{ maxWidth: '780px', margin: '0 auto' }}>
        <h2
          style={{
            fontSize: 'clamp(1.2rem, 3vw, 1.6rem)',
            fontWeight: 800,
            color: accentColor,
            marginBottom: '8px',
            letterSpacing: '-0.02em',
          }}
        >
          {title}
        </h2>
        <p style={{ color: 'var(--faq-muted, #94a3b8)', fontSize: '0.85rem', marginBottom: '28px' }}>
          Las preguntas más buscadas por nuestra comunidad
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {items.map((item, i) => (
            <div
              key={i}
              style={{
                background: 'var(--faq-card, #1e293b)',
                borderRadius: '10px',
                border: open === i ? `1px solid ${accentColor}40` : '1px solid var(--faq-border, #1e293b)',
                overflow: 'hidden',
                transition: 'border-color 0.15s',
              }}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  padding: '14px 18px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  color: 'var(--faq-text, #f1f5f9)',
                }}
                aria-expanded={open === i}
              >
                <span
                  itemProp="name"
                  style={{
                    fontSize: '0.9rem',
                    fontWeight: open === i ? 700 : 500,
                    lineHeight: '1.4',
                    color: open === i ? accentColor : 'inherit',
                  }}
                >
                  {item.q}
                </span>
                <span
                  style={{
                    flexShrink: 0,
                    fontSize: '1.1rem',
                    color: accentColor,
                    transform: open === i ? 'rotate(45deg)' : 'none',
                    transition: 'transform 0.2s',
                    lineHeight: 1,
                  }}
                >
                  +
                </span>
              </button>

              {open === i && (
                <div
                  style={{ padding: '0 18px 16px' }}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <p
                    itemProp="text"
                    style={{
                      fontSize: '0.85rem',
                      lineHeight: '1.65',
                      color: 'var(--faq-muted, #94a3b8)',
                      borderTop: `1px solid var(--faq-border, #1e293b)`,
                      paddingTop: '12px',
                      margin: 0,
                    }}
                  >
                    {item.a}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media (prefers-color-scheme: light) {
          section[aria-label="Preguntas frecuentes"] {
            --faq-bg: #f8fafc;
            --faq-border: #e2e8f0;
            --faq-card: #ffffff;
            --faq-text: #0f172a;
            --faq-muted: #64748b;
          }
        }
        :root[data-theme="light"] section[aria-label="Preguntas frecuentes"] {
          --faq-bg: #f8fafc;
          --faq-border: #e2e8f0;
          --faq-card: #ffffff;
          --faq-text: #0f172a;
          --faq-muted: #64748b;
        }
        :root[data-theme="dark"] section[aria-label="Preguntas frecuentes"] {
          --faq-bg: #0f172a;
          --faq-border: #1e293b;
          --faq-card: #1e293b;
          --faq-text: #f1f5f9;
          --faq-muted: #94a3b8;
        }
      `}</style>
    </section>
  );
};
